'use client';

import { updateCart } from '@/api/cart';
import { ICartItem } from '@/api/types/cart';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { toast } from '@/components/Toast';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { handlerPrescriptionData } from '@/utils/prescription';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CheckOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCartSensorsTracking } from '../../hooks/useCartSensorsTracking';

// 扩展ICartItem类型以包含lens相关数据
interface ICartItemWithLens extends ICartItem {
  lens?: ICartItem['lens'] & {
    new_lens?: {
      lens_id?: number;
      lens_code?: string;
      lens_price?: Array<{
        currency?: string;
        price?: string;
        sell_price?: string;
      }>;
    };
  };
  option?: ICartItem['option'] & {
    old_lens_id?: string;
    old_lens_price?: Array<{
      currency?: string;
      price?: string;
      sell_price?: string;
    }>;
    old_lens_status?: number;
  };
}

interface BlueLightItemProps {
  cartItemData: ICartItemWithLens;
  onReloadCart: (data?: any) => void;
  className?: string;
}

export default function BlueLightItem({
  cartItemData,
  onReloadCart,
  className,
}: BlueLightItemProps) {
  const t = useTranslations('cart.blueLightItem');
  const { deviceType } = useContext(ResponsiveContext);
  const { currency } = useStore((state) => state);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  // 获取埋点函数
  const { trackLensUpgradeClick, trackLensUpgradeView } = useCartSensorsTracking();

  const [blueConfirmPopup, setBlueConfirmPopup] = useState(false);

  // 使用 ref 追踪是否已发送过首次展示埋点
  const hasTrackedInitialViewRef = useRef(false);

  // 组件挂载时，检查是否需要发送首次展示埋点
  useEffect(() => {
    if (hasTrackedInitialViewRef.current) return;

    // 检查是否有镜片升级数据
    const hasOldLens = Boolean(cartItemData.option?.old_lens_id);
    const hasNewLens = Boolean(cartItemData.lens?.new_lens);

    if (hasOldLens || hasNewLens) {
      hasTrackedInitialViewRef.current = true;

      // 延迟执行确保组件完全渲染
      setTimeout(() => {
        let upgradeType = '';
        let upgradeLensCode = '';

        if (hasOldLens) {
          upgradeType = '取消升级';
          upgradeLensCode = cartItemData.lens?.lens_code || '';
        } else if (hasNewLens) {
          upgradeType = '应用升级';
          upgradeLensCode = cartItemData.lens?.new_lens?.lens_code || '';
        }

        if (upgradeType && upgradeLensCode) {
          trackLensUpgradeView(cartItemData as any, upgradeType, upgradeLensCode);
        }
      }, 100);
    }
  }, []); // 只在组件挂载时执行一次

  // 判断是否为PC端
  const isWeb = deviceType === EPlatform.WEB;

  // 计算升级前后差价
  const diffPrice = useMemo(() => {
    try {
      let blueSellPrice = 0;

      // 如果已经有老镜片ID，说明已经升级过，从old_lens_price取值
      if (cartItemData.option?.old_lens_id && cartItemData.option?.old_lens_price) {
        const oldLensPrice = cartItemData.option.old_lens_price.find(
          (item: any) => item.currency === currency?.name,
        );
        blueSellPrice = Number(oldLensPrice?.sell_price || 0);
      }
      // 否则从new_lens取值
      else if (cartItemData.lens?.new_lens?.lens_price) {
        const newLensPrice = cartItemData.lens.new_lens.lens_price.find(
          (item: any) => item.currency === currency?.name,
        );
        blueSellPrice = Number(newLensPrice?.sell_price || 0);
      }

      // 当前镜片价格
      const currentLensPrice = cartItemData.lens?.lens_price?.find(
        (item: any) => item.currency === currency?.name,
      );
      const currentSellPrice = Number(currentLensPrice?.sell_price || 0);

      const diff = blueSellPrice - currentSellPrice;
      return Math.abs(Math.round(diff * 100) / 100).toFixed(2);
    } catch (error) {
      logger.error('Failed to calculate diff price:', error);
      return '0.00';
    }
  }, [cartItemData, currency?.name]);

  // 处理镜片升级/降级
  const handleLensChange = async (skipConfirm = false) => {
    const option = { ...cartItemData.option };

    // 回滚时，如果老镜片不可用，需要弹出确认弹窗
    if (option.old_lens_id && (option as any).old_lens_status === 2 && !skipConfirm) {
      setBlueConfirmPopup(true);
      return;
    }

    setIsGlobalLoading(true);

    try {
      let hasOldLens = false;
      let upgradeLensCode = '';

      // 如果有老镜片，说明这里是要回滚镜片
      if (option.old_lens_id) {
        hasOldLens = true;
        option.lens_id = option.old_lens_id;
        // 移除 old_lens_id 字段
        delete option.old_lens_id;
        delete (option as any).old_lens_price;
        upgradeLensCode = cartItemData.lens?.lens_code || '';
      }
      // 如果有新镜片，说明这里是要升级
      else if (cartItemData.lens?.new_lens) {
        option.old_lens_id = String(option.lens_id);
        option.lens_id = String(cartItemData.lens.new_lens.lens_id);
        upgradeLensCode = cartItemData.lens.new_lens.lens_code || '';
      }

      // 使用handlerPrescriptionData方法整合数据
      const params = handlerPrescriptionData({
        cart_id: cartItemData.cart_id,
        product_id: cartItemData.product_id,
        qty: cartItemData.qty,
        option,
        prescriptions_image: cartItemData.prescriptions_image || '',
      });

      // 如果有老镜片，添加 is_rollback_lens: 1 参数
      if (hasOldLens) {
        params.is_rollback_lens = 1;
      } else {
        params.option.old_lens_price = cartItemData.lens?.lens_price;
      }

      // 🔍 发送神策埋点 - 镜片升级点击
      trackLensUpgradeClick(
        cartItemData as any,
        hasOldLens ? '取消升级' : '应用升级',
        upgradeLensCode,
      );

      const { code, data } = await updateCart(params);

      if (code === 200) {
        onReloadCart(data);

        // 点击升级成功后发送LensUpgrade_View埋点
        const newUpgradeType = hasOldLens ? '应用升级' : '取消升级';
        trackLensUpgradeView(cartItemData as any, newUpgradeType, upgradeLensCode);
      } else {
        onReloadCart();
      }

      // 注释：LensUpgrade_View埋点已经在上面处理
    } catch (error) {
      logger.error('Failed to update lens:', error);
      toast.error(t('operationFailed'));
      onReloadCart();
    } finally {
      setIsGlobalLoading(false);
    }
  };

  // 取消确认弹窗
  const handleCancelConfirm = () => {
    setBlueConfirmPopup(false);
  };

  // 确认弹窗
  const handleConfirmUpgrade = async () => {
    setBlueConfirmPopup(false);
    await handleLensChange(true);
  };

  // 检查是否已添加防蓝光
  const isBlueAdded = Boolean(cartItemData.option?.old_lens_id);

  return (
    <>
      <div className={`${className || ''}`}>
        <div
          className={`
            ${
              isWeb
                ? 'border-t border-[#eeeeee] pt-[24px] mt-[18px] ml-[40px] flex justify-between items-center'
                : 'border-t border-[#eeeeee] pt-[11px] mr-[12px] mt-[12px] flex justify-between items-center'
            }
          `}
        >
          {/* 左侧内容 */}
          <div className={isWeb ? 'w-auto flex items-center' : 'w-[260px]'}>
            {isWeb ? (
              // PC端布局
              <>
                <Image
                  src="/cart/blue-light-icon.png"
                  alt={t('title')}
                  width={40}
                  height={40}
                  className="w-[40px] h-auto mr-[12px]"
                />
                <div>
                  <div className="flex items-center">
                    <span className="font-bb text-[16px]">{t('title')}</span>
                    <span className="font-br text-[16px] ml-[8px] notranslate">
                      (+ {formatPriceNumberWithSymbol(diffPrice, currency)})
                    </span>
                  </div>
                  <p className="text-[#808080] text-[14px]">{t('description')}</p>
                </div>
              </>
            ) : (
              // 移动端布局
              <div>
                <div className="flex items-center">
                  <Image
                    src="/cart/blue-light-icon.png"
                    alt={t('title')}
                    width={19}
                    height={19}
                    className="w-[19px] h-auto mr-[6px]"
                  />
                  <span className="font-bs" style={{ fontSize: '13px' }}>
                    {t('title')}
                  </span>
                  <span className="font-br ml-[4px] notranslate" style={{ fontSize: '13px' }}>
                    (+ {formatPriceNumberWithSymbol(diffPrice, currency)})
                  </span>
                </div>
                <p className="mt-[6px] text-[#808080] text-[12px]">{t('description')}</p>
              </div>
            )}
          </div>

          {/* 右侧按钮 */}
          <div
            className={`
              ${
                isWeb
                  ? 'h-[32px] px-[10px] min-w-[72px] rounded-[16px] text-[14px]'
                  : 'h-[24px] px-[10px] min-w-[55px] rounded-[12px] text-[12px]'
              }
              ${
                isBlueAdded
                  ? 'text-primary-purple bg-white border border-primary-purple'
                  : 'text-white bg-primary-purple border border-primary-purple'
              }
              flex items-center justify-center font-bb cursor-pointer box-border
            `}
            onClick={() => handleLensChange()}
          >
            {isBlueAdded ? (
              <>
                <CheckOutlined className={`${isWeb ? 'mr-[6px]' : ''} text-[20px]`} />
                <span>{t('added')}</span>
              </>
            ) : (
              <span>{t('add')}</span>
            )}
          </div>
        </div>
      </div>

      {/* 确认弹窗 - 双端统一使用Dialog */}
      <Dialog
        open={blueConfirmPopup}
        onClose={handleCancelConfirm}
        title=""
        footer={null}
        sx={{
          '& .MuiDialog-paper': {
            width: isWeb ? '400px' : '86vw',
            borderRadius: isWeb ? '12px' : '16px',
            padding: '0px',
          },
        }}
      >
        <div className="text-center">
          <p className={`${isWeb ? 'text-[16px] mb-[48px]' : 'text-[14px] mb-[32px]'}`}>
            {t('confirmMessage')}
          </p>
          <div className={`flex justify-between`}>
            <Button
              variant="outlined"
              onClick={handleCancelConfirm}
              className={`${
                isWeb
                  ? 'w-[160px] h-[48px] rounded-[4px] !text-[16px] !font-bb'
                  : '!w-[132px] !h-[43px] rounded-[50px] !text-[14px] !font-bb'
              }`}
              sx={{
                border: '1px solid #282828',
                color: '#282828',
                borderColor: undefined,
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmUpgrade}
              className={`${
                isWeb
                  ? 'w-[160px] h-[48px] rounded-[4px] !text-[16px] !font-bb'
                  : '!w-[132px] !h-[43px] rounded-[50px] !text-[14px] !font-bb'
              }`}
            >
              {t('confirm')}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
